/**
 * Parça Seç Modal - service-contents'ten kategorilere göre parça listesi
 * Her parçada: fotoğraf veya "Görsel yok" ikonu + tooltip, checkbox, ad, kod
 */
import React, { useState, useEffect, useCallback } from 'react';
import { fetchMobileServiceContents } from '../services/mobileAppointmentService';
import { getParcaFotoUrl } from '../services/productsService';

const ParcaSecModal = ({ isOpen, onClose, servisKaydiId, onSeçilenleriEkle }) => {
  const [parcalarKategorili, setParcalarKategorili] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedKategoriler, setExpandedKategoriler] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set()); // icerik _id

  const loadContents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchMobileServiceContents(servisKaydiId || null);
      const list = res?.veri?.parcalar ?? res?.veri?.parcalar ?? [];
      setParcalarKategorili(Array.isArray(list) ? list : []);
      // İlk kategoriyi aç
      if (list?.length) {
        setExpandedKategoriler(new Set(list.map((k) => k.kategoriId || k._id || k.kategoriAdi).filter(Boolean)));
      }
    } catch {
      setParcalarKategorili([]);
    } finally {
      setLoading(false);
    }
  }, [servisKaydiId]);

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
      loadContents();
    }
  }, [isOpen, loadContents]);

  const toggleKategori = (key) => {
    setExpandedKategoriler((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelect = (icerikId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(icerikId)) next.delete(icerikId);
      else next.add(icerikId);
      return next;
    });
  };

  const handleSeçilenleriEkle = () => {
    const secilenler = [];
    parcalarKategorili.forEach((kat) => {
      (kat.icerikler || []).forEach((ic) => {
        const icId = ic._id || ic.id;
        if (icId && selectedIds.has(icId)) {
          secilenler.push({
            parcaAdi: ic.icerikAdi || ic.urunAdi || ic.ad,
            parcaKodu: ic.icerikKodu || ic.urunKodu || ic.kod,
            adet: 1,
            fiyat: ic.fiyat ?? ic.fiyatgaranti ?? 0,
            icerikId: icId,
            fotoUrl: ic.fotoUrl,
          });
        }
      });
    });
    onSeçilenleriEkle?.(secilenler);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px', width: '95%', maxHeight: '85vh' }}>
        <div className="modal-header">
          <h2>Parça Seç</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', padding: '16px' }}>
          {loading ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-gray)' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginRight: '8px' }}></i>
              Yükleniyor...
            </div>
          ) : parcalarKategorili.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-gray)' }}>
              Parça kategorisi bulunamadı.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {parcalarKategorili.map((kat, katIdx) => {
                const katKey = kat.kategoriId || kat._id || kat.kategoriAdi || `kat-${katIdx}`;
                const icerikler = kat.icerikler ?? [];
                const isExpanded = expandedKategoriler.has(katKey);

                return (
                  <div key={katKey} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                    <button
                      type="button"
                      onClick={() => toggleKategori(katKey)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: '#f8fafc',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        textAlign: 'left',
                        fontWeight: 600,
                        color: 'var(--text-dark)',
                        fontSize: '14px',
                      }}
                    >
                      <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ fontSize: '12px', color: '#64748b' }}></i>
                      {kat.kategoriAdi || kat.ad || 'Kategori'}
                    </button>
                    {isExpanded && (
                      <div style={{ padding: '8px 0', background: '#fff' }}>
                        {icerikler.length === 0 ? (
                          <div style={{ padding: '12px 16px', fontSize: '13px', color: '#64748b' }}>Bu kategoride parça yok.</div>
                        ) : (
                          icerikler.map((ic) => {
                            const icId = ic._id || ic.id;
                            const checked = selectedIds.has(icId);
                            const fotoUrl = getParcaFotoUrl(ic.fotoUrl);
                            const ad = ic.icerikAdi || ic.urunAdi || ic.ad || '—';
                            const kod = ic.icerikKodu || ic.urunKodu || ic.kod || '';

                            return (
                              <label
                                key={icId}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  padding: '10px 16px',
                                  cursor: 'pointer',
                                  transition: 'background 0.15s',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#f8fafc';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'transparent';
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleSelect(icId)}
                                  style={{ width: '18px', height: '18px', flexShrink: 0 }}
                                />
                                {/* Parça fotoğrafı veya "Görsel yok" ikonu + tooltip - her zaman görünür */}
                                <div
                                  style={{
                                    width: 48,
                                    height: 48,
                                    flexShrink: 0,
                                    flexGrow: 0,
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    background: fotoUrl ? 'transparent' : '#e2e8f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 48,
                                    minHeight: 48,
                                  }}
                                  title={fotoUrl ? ad : 'Görsel yok'}
                                  role="img"
                                  aria-label={fotoUrl ? ad : 'Görsel yok'}
                                >
                                  {fotoUrl ? (
                                    <img
                                      src={fotoUrl}
                                      alt={ad}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                    />
                                  ) : (
                                    <span title="Görsel yok" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                                      <i className="fas fa-image" style={{ fontSize: '22px', color: '#94a3b8', opacity: 0.6 }} aria-hidden="true" />
                                    </span>
                                  )}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 500, color: 'var(--text-dark)', fontSize: '14px' }}>{ad}</div>
                                  {kod && (
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                      Kod: {kod}
                                    </div>
                                  )}
                                </div>
                              </label>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ padding: '16px', borderTop: '1px solid #e2e8f0' }}>
          <button type="button" className="secondary-btn" onClick={onClose}>
            İptal
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={handleSeçilenleriEkle}
            disabled={selectedIds.size === 0}
          >
            Seçilenleri Ekle ({selectedIds.size})
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParcaSecModal;
